import { Query, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbarContext } from 'hooks/useSnackbarContext';

type UseCrudMutationOptions<TData, TVariables> = {
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage?: string;
  errorMessage: string;
  invalidateKeys?: ReadonlyArray<readonly unknown[]>;
  invalidatePredicate?: (query: Query) => boolean;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: Error) => void;
};

function useCrudMutation<TData, TVariables>({
  mutationFn,
  successMessage,
  errorMessage,
  invalidateKeys,
  invalidatePredicate,
  onSuccess,
  onError,
}: UseCrudMutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  return useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
      if (successMessage) {
        showSnackbar({ text: successMessage, severity: 'success' });
      }
      const invalidations = (invalidateKeys ?? []).map(queryKey => queryClient.invalidateQueries({ queryKey }));
      if (invalidatePredicate) {
        invalidations.push(queryClient.invalidateQueries({ predicate: invalidatePredicate }));
      }
      return Promise.all(invalidations);
    },
    onError: error => {
      onError?.(error);
      showSnackbar({ text: errorMessage, severity: 'danger' });
    },
  });
}

export default useCrudMutation;
